import type { CartDataFragment } from '@order/graphql';
import type { DeliveryOrderDetailInput } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

export const useOrderValidation = ({
  wantedTime,
  dropoffLocationId,
  billingAccountId,
  cart,
  tip,
  deliveryOrderDetail,
}: CanSubmitOrderProps) => {
  // Wanted Time
  const hasWantedTime = Boolean(wantedTime);

  // Dropoff Locations
  const hasMultipleDropoffLocations =
    (cart?.restaurant?.availableDropOffLocations ?? []).length > 1;
  const isNotDelivery = cart?.orderType !== 'DELIVERY';
  const needsDropoffLocation = isNotDelivery && hasMultipleDropoffLocations;

  const isDropoffSelected =
    Boolean(dropoffLocationId) && dropoffLocationId !== 'placeholder';
  const hasDropoffSelectionIfNeeded = needsDropoffLocation
    ? isDropoffSelected
    : true;

  // Billing Account
  const hasBillingAccountSelected = billingAccountId !== 'ADD';

  // Submit Order Validations

  const orderType = cart?.orderType;
  const cartSubtotal = cart?.ledger?.subtotal ?? 0;
  const orderTip = orderType === 'DELIVERY' ? deliveryOrderDetail?.tip : tip;

  const hasAcceptableTipAmount = checkIfHasAcceptableTipAmount({
    tip: orderTip,
    cartSubtotal,
  });

  const shouldBlockCheckoutOnPress =
    !hasAcceptableTipAmount ||
    !hasWantedTime ||
    !hasBillingAccountSelected ||
    !hasDropoffSelectionIfNeeded;
  const cannotSubmitOrderReason = useCannotSubmitOrderReason({
    hasWantedTime,
    hasDropoffSelectionIfNeeded,
    hasBillingAccountSelected,
    hasAcceptableTipAmount,
    cart,
  });

  return {
    shouldBlockCheckoutOnPress,
    cannotSubmitOrderReason,
  };
};

const useCannotSubmitOrderReason = ({
  hasWantedTime,
  hasDropoffSelectionIfNeeded,
  hasBillingAccountSelected,
  hasAcceptableTipAmount,
  cart,
}: CannotSubmitOrderReasonProps) => {
  const { t } = useLocalizationContext();

  if (!hasAcceptableTipAmount) {
    return t('checkout.cannot-submit.max-tip');
  }

  if (!hasWantedTime) {
    return t('checkout.cannot-submit.wanted-time');
  }

  if (!hasDropoffSelectionIfNeeded) {
    return cart?.restaurant?.isOutpost
      ? t('checkout.cannot-submit.drop-off')
      : t('checkout.cannot-submit.pickup');
  }

  if (!hasBillingAccountSelected) {
    return t('checkout.cannot-submit.billing-account');
  }

  return t('general.error');
};

// ─── Utils ───────────────────────────────────────────────────────────────────

/**
 * Checks whether the provided order tip does not exceed order subtotal.
 *
 * NOTE: The maximum tip for the order is 100%.
 * NOTE: Tipping is available only for `pickup` and `delivery` orders.
 */
function checkIfHasAcceptableTipAmount(
  params: CheckIfHasAcceptableTipAmountParams,
) {
  const { cartSubtotal, tip } = params;
  const orderTip = typeof tip === 'number' ? tip : 0;

  return orderTip <= cartSubtotal;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type CanSubmitOrderProps = {
  readonly wantedTime?: string | null;
  readonly dropoffLocationId?: string | null;
  readonly billingAccountId?: string | null;
  readonly cart?: Partial<CartDataFragment> | null;
  readonly tip?: number | null;
  readonly deliveryOrderDetail?: DeliveryOrderDetailInput | null;
};

type CannotSubmitOrderReasonProps = {
  readonly hasWantedTime: boolean;
  readonly hasDropoffSelectionIfNeeded: boolean;
  readonly hasBillingAccountSelected: boolean;
  readonly hasAcceptableTipAmount: boolean;
  readonly cart?: Partial<CartDataFragment> | null;
};

type CheckIfHasAcceptableTipAmountParams = {
  readonly tip: number | null | undefined;
  readonly cartSubtotal: number;
};
